import { FrontendConstants } from '../common/FrontendConstants';
import { DeleteIndexedDB, GetIndexedDB, SetIndexedDB } from '../common/Helpers';
import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import { Logger } from '@shared/common';
import type { IGlobalSettingsDTO, ILobbySettingsDTO, ILocalizationDetailDTO, ILocalizationDTO, IPortalSettingsDTO, LocalizationKind } from '@shared/common';

/** File-local constants for LobbyClient. */
const localConstants = {
  logContext: 'LobbyClient',
  // Multipart form field carrying an uploaded settings media file (must match the lobby SettingsController FileInterceptor field name; shared by audio and video).
  mediaFileField: 'file',
  // IndexedDB key prefix for cached translation results. Versioned by Settings.VERSION so a deploy invalidates every cached entry; the content fingerprint inside the key invalidates on any
  // source-text change. Key shape: `translate:<VERSION>:<source>:<target>:<context>:<contentFingerprint>`.
  translateCachePrefix: 'translate:',
  translateCacheKeySeparator: ':',
  // Fingerprint serialiser delimiters — control chars that cannot legally appear inside JSON-encoded user content, so two distinct content maps cannot collide via concatenation.
  fingerprintFieldDelimiter: '\u0001',
  fingerprintEntryDelimiter: '\u0002',
  // Number of leading hex characters of SHA-256 to retain as the fingerprint. 16 hex = 64 bits of collision resistance, more than enough for ~hundreds of source/locale pairs per client.
  fingerprintLength: 16,
  hashAlgorithmSha256: 'SHA-256',
  // Algorithm tags embedded in the cache key so entries hashed with different fingerprint algorithms cannot alias each other. A browser without `crypto.subtle` will only ever produce `fnv` keys;
  // a browser with it will only ever produce `sha` keys. Both coexist safely if the same IndexedDB is later accessed from a context with different capabilities.
  fingerprintAlgorithmTagSha: 'sha',
  fingerprintAlgorithmTagFnv: 'fnv',
  // FNV-1a 32-bit offset basis and prime. Two passes (forward + reverse-byte) produce ~64 bits of effective state, matching the SHA-256 truncation width above.
  fnvOffsetBasis: 0x811c9dc5,
  fnvPrime: 0x01000193,
  fnvHexPadLength: 8,
  logTranslateCacheParseErr: 'Translation cache entry corrupt; evicting and refetching: ',
  logTranslateCacheKeyErr: 'Translation cache key build failed; degrading to network: ',
  logTranslateCacheReadErr: 'Translation cache read failed; degrading to network: '
} as const;

/** Client for lobby service operations including lobbies, localization, and cache management. */
export class LobbyClient {
  /** Singleton instance. */
  private static instance: LobbyClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton LobbyClient instance. */
  public static GetInstance(): LobbyClient {
    return LobbyClient.instance ?? (LobbyClient.instance = new LobbyClient());
  }

  // Service Health

  /**
   * GET /api/lobby/base/health - Service health check.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns 'OK' if the service is healthy.
   */
  public async HealthCheck(signal: AbortSignal): Promise<string> {
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'base/health', { signal });
    if (!res.ok) throw new Error('Lobby service health check failed');
    return await res.text();
  }

  // Cache Clear

  /**
   * GET /api/lobby/lobbies/gated/clear - Clear lobby-related caches.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns 'CLEARED' confirmation string.
   */
  public async ClearLobbies(signal: AbortSignal): Promise<string> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'lobbies/gated/clear', { signal }, auth);
    if (!res.ok) throw new Error('Failed to clear lobby cache');
    return await res.text();
  }

  /**
   * GET /api/lobby/localization/gated/clear - Clear localization-related caches.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns 'CLEARED' confirmation string.
   */
  public async ClearLocalization(signal: AbortSignal): Promise<string> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'localization/gated/clear', { signal }, auth);
    if (!res.ok) throw new Error('Failed to clear localization cache');
    return await res.text();
  }

  /**
   * POST /api/lobby/localization/gated/:type/:code/clear - Clear a single localization source cache.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param type - Localization kind.
   * @param code - App code.
   * @returns Number of cache entries removed.
   */
  public async ClearLocalizationSource(signal: AbortSignal, type: LocalizationKind, code: string): Promise<number> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `localization/gated/${type}/${code}/clear`, { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to clear localization source cache');
    return await res.json();
  }

  /**
   * POST /api/lobby/localization/gated/:type/:code/:locale/clear - Clear one locale cache entry for one localization source.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param type - Localization kind.
   * @param code - App code.
   * @param locale - Target locale.
   * @returns Number of cache entries removed.
   */
  public async ClearLocalizationLocale(signal: AbortSignal, type: LocalizationKind, code: string, locale: string): Promise<number> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `localization/gated/${type}/${code}/${locale}/clear`, { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to clear localization locale cache');
    return await res.json();
  }

  // Localizations (LocalizationController)

  /**
   * GET /api/lobby/localization/gated - Get all localization sources.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns Array of localization summaries.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GetLocalizations(signal: AbortSignal): Promise<ILocalizationDTO[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'localization/gated', { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch localizations');
    return await res.json();
  }

  /**
   * GET /api/lobby/localization/gated/:type/:code - Get one localization source detail.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param type - Localization kind.
   * @param code - App code.
   * @returns The localization detail payload.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GetLocalizationDetail(signal: AbortSignal, type: LocalizationKind, code: string): Promise<ILocalizationDetailDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `localization/gated/${type}/${code}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch localization detail');
    return await res.json();
  }

  /**
   * POST /api/lobby/localization/gated/translate-all - Translate all missing locales across all sources.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns Number of locales translated.
   */
  public async TranslateAllLocalizations(signal: AbortSignal): Promise<number> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'localization/gated/translate-all', { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to translate all localizations');
    return await res.json();
  }

  /**
   * POST /api/lobby/localization/gated/:type/:code/translate-all - Translate all missing locales for one source.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param type - Localization kind.
   * @param code - App code.
   * @returns Number of locales translated.
   */
  public async TranslateLocalizationSource(signal: AbortSignal, type: LocalizationKind, code: string): Promise<number> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `localization/gated/${type}/${code}/translate-all`, { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to translate localization source');
    return await res.json();
  }

  /**
   * POST /api/lobby/localization/gated/:type/:code/:locale/translate - Translate one locale for one source.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param type - Localization kind.
   * @param code - App code.
   * @param locale - Target locale.
   * @returns Number of locales translated.
   */
  public async TranslateLocalization(signal: AbortSignal, type: LocalizationKind, code: string, locale: string): Promise<number> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `localization/gated/${type}/${code}/${locale}/translate`, { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to translate localization');
    return await res.json();
  }

  // Settings (SettingsController)

  /**
   * GET /api/lobby/settings/gated/clear - Clear settings-related caches.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns 'CLEARED' confirmation string.
   */
  public async ClearSettings(signal: AbortSignal): Promise<string> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'settings/gated/clear', { signal }, auth);
    if (!res.ok) throw new Error('Failed to clear settings cache');
    return await res.text();
  }

  /**
   * GET /api/lobby/settings/portal - Get public portal access gate (no auth required).
   *
   * Returns `envAccessBlocked` based on the Portal IP gate: only IPs covered by `envTestWhitelist` are allowed, and `envProdBlacklist` also blocks in Production.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The public portal settings.
   */
  public async GetPortalSettings(signal: AbortSignal): Promise<IPortalSettingsDTO> {
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'settings/portal', { signal });
    if (!res.ok) throw new Error('Failed to fetch portal settings');
    return await res.json();
  }

  /**
   * GET /api/lobby/settings/lobby - Get public lobby access gate (no auth required).
   *
   * Returns `envAccessBlocked` based on the env-scoped lobby IP gate: Test allows only `envTestWhitelist` IPs, Production blocks `envProdBlacklist` IPs, and Development never blocks.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The public lobby settings.
   */
  public async GetLobbySettings(signal: AbortSignal): Promise<ILobbySettingsDTO> {
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'settings/lobby', { signal });
    if (!res.ok) throw new Error('Failed to fetch lobby settings');
    return await res.json();
  }

  /**
   * Builds the absolute URL of the public demo audio stream (GET /api/lobby/settings/lobby/audio). The endpoint needs no auth, so the URL can be used directly as an `<audio src>`.
   *
   * @param version - Cache-busting token (the audio storage key) appended as `?v=`, so a replaced file is re-fetched.
   * @returns The absolute audio stream URL.
   */
  public GetLobbyAudioUrl(version: string): string {
    return `${Settings.SERVICE_LOBBY_URI}/settings/lobby/audio?v=${encodeURIComponent(version)}`;
  }

  /**
   * Builds the absolute URL of the public demo video stream (GET /api/lobby/settings/lobby/video). The endpoint needs no auth, so the URL can be used directly as a `<video src>`.
   *
   * @param version - Cache-busting token (the video storage key) appended as `?v=`, so a replaced file is re-fetched.
   * @returns The absolute video stream URL.
   */
  public GetLobbyVideoUrl(version: string): string {
    return `${Settings.SERVICE_LOBBY_URI}/settings/lobby/video?v=${encodeURIComponent(version)}`;
  }

  /**
   * GET /api/lobby/settings/gated - Get global settings (privileged).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The global settings document.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedGetSettings(signal: AbortSignal): Promise<IGlobalSettingsDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'settings/gated', { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch settings');
    return await res.json();
  }

  /**
   * PUT /api/lobby/settings/gated - Update global settings (privileged).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param updateData - Partial settings data with fields to update.
   * @returns The updated global settings document.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedUpdateSettings(signal: AbortSignal, updateData: Partial<IGlobalSettingsDTO>): Promise<IGlobalSettingsDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      'settings/gated',
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(updateData),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to update settings');
    return await res.json();
  }

  /**
   * POST /api/lobby/settings/gated/audio - Upload (or replace) the platform audio file (admin).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param file - The audio file to upload.
   * @returns The updated global settings document.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedUploadAudio(signal: AbortSignal, file: File): Promise<IGlobalSettingsDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const form = new FormData();
    form.append(localConstants.mediaFileField, file);
    // No Content-Type header: the browser sets the multipart boundary itself.
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'settings/gated/audio', { method: FrontendConstants.MethodPost, body: form, signal }, auth);
    if (!res.ok) throw new Error('Failed to upload settings audio');
    return await res.json();
  }

  /**
   * GET /api/lobby/settings/gated/audio - Fetch the stored platform audio bytes (privileged).
   *
   * Fetched as a Blob (not a bare URL) because the endpoint is auth-gated and an `<audio src>` cannot carry the bearer token; the caller turns the blob into an object URL for playback. The
   * current audio key is appended as a `v` cache-bust param so each uploaded version is a distinct URL: normal caching stays efficient, yet a replace (new key) is always fetched fresh rather
   * than served stale.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param version - The current audio key, used to make the URL version-specific.
   * @returns The audio file contents as a Blob.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedGetAudio(signal: AbortSignal, version: string): Promise<Blob> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `settings/gated/audio?v=${encodeURIComponent(version)}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch settings audio');
    return await res.blob();
  }

  /**
   * DELETE /api/lobby/settings/gated/audio - Remove the stored platform audio file (admin).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The updated global settings document.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedRemoveAudio(signal: AbortSignal): Promise<IGlobalSettingsDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'settings/gated/audio', { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) throw new Error('Failed to remove settings audio');
    return await res.json();
  }

  /**
   * POST /api/lobby/settings/gated/video/upload-url - Request a presigned URL for uploading (or replacing) the platform video file directly to object storage (admin).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param contentType - The MIME type of the video file.
   * @param contentLength - The exact byte size of the video file.
   * @returns The presigned PUT URL and the logical storage key to confirm.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedGetVideoUploadUrl(signal: AbortSignal, contentType: string, contentLength: number): Promise<{ url: string; key: string }> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      'settings/gated/video/upload-url',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify({ contentType, contentLength }),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to request settings video upload URL');
    return await res.json();
  }

  /**
   * POST /api/lobby/settings/gated/video/confirm - Confirm a direct video upload and persist its storage key in global settings (admin).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param key - The logical storage key returned by {@link GatedGetVideoUploadUrl}.
   * @returns The updated global settings document.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedConfirmVideoUpload(signal: AbortSignal, key: string): Promise<IGlobalSettingsDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      'settings/gated/video/confirm',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify({ key }),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to confirm settings video upload');
    return await res.json();
  }

  /**
   * Upload (or replace) the platform video file by requesting a presigned URL, uploading directly to object storage, and confirming with the lobby service.
   *
   * This bypasses the Cloud Run 32 MiB HTTP/1 request limit because the video bytes never travel through the lobby service.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param file - The video file to upload.
   * @returns The updated global settings document.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedUploadVideo(signal: AbortSignal, file: File): Promise<IGlobalSettingsDTO> {
    const { url, key } = await this.GatedGetVideoUploadUrl(signal, file.type, file.size);

    const uploadRes = await fetch(url, {
      method: FrontendConstants.MethodPut,
      body: file,
      headers: { [FrontendConstants.HeaderContentType]: file.type },
      credentials: 'omit',
      signal
    });
    if (!uploadRes.ok) throw new Error('Failed to upload settings video to storage');

    return await this.GatedConfirmVideoUpload(signal, key);
  }

  /**
   * GET /api/lobby/settings/gated/video - Fetch a signed URL for the stored platform video (privileged).
   *
   * The service returns a short-lived presigned URL that the caller can load directly from object storage, bypassing the Cloud Run 32 MiB response limit. The current video key is appended as a
   * `v` cache-bust param so each uploaded version is treated as a distinct URL: normal caching stays efficient, yet a replace (new key) is always fetched fresh rather than served stale.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param version - The current video key, used to make the URL version-specific.
   * @returns A presigned URL for the video, or `null` when no video has been uploaded (404).
   * @throws Error when the request fails or responds with a non-OK status other than 404.
   */
  public async GatedGetVideo(signal: AbortSignal, version: string): Promise<string | null> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `settings/gated/video?v=${encodeURIComponent(version)}`, { signal }, auth);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error('Failed to fetch settings video');
    const payload = (await res.json()) as { url: string };
    return payload.url;
  }

  /**
   * DELETE /api/lobby/settings/gated/video - Remove the stored platform video file (admin).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The updated global settings document.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedRemoveVideo(signal: AbortSignal): Promise<IGlobalSettingsDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'settings/gated/video', { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) throw new Error('Failed to remove settings video');
    return await res.json();
  }

  // Voice catalog management

  /**
   * GET /api/lobby/voices/gated - Get the merged voice catalog (defaults + overrides).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The voice catalog with current overrides applied.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedGetVoices(signal: AbortSignal): Promise<{
    catalog: { provider: string; option: string; voiceId: string }[];
    overrides: Record<string, Record<string, string>>;
    health: { provider: string; option: string; voiceId: string; status: 'ok' | 'failed'; reason?: string; checkedAt: string }[];
  }> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'voices/gated', { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch voice catalog');
    return await res.json();
  }

  /**
   * PUT /api/lobby/voices/gated - Update voice overrides.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param overrides - The voice overrides to persist.
   * @returns The updated global settings document.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedUpdateVoices(signal: AbortSignal, overrides: Record<string, Record<string, string>>): Promise<IGlobalSettingsDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      'voices/gated',
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(overrides),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to update voice overrides');
    return await res.json();
  }

  /**
   * POST /api/lobby/voices/gated/check - Run health checks against all voice mappings.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns Per-voice health check results.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async GatedCheckVoices(signal: AbortSignal): Promise<{ provider: string; option: string; voiceId: string; status: 'ok' | 'failed'; reason?: string; checkedAt: string }[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'voices/gated/check', { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to check voice health');
    return await res.json();
  }

  // Localization (LocalizationController)

  /**
   * POST /api/lobby/localization/translate - Translate JSON content between languages. Backed by a per-client IndexedDB cache so repeat language switches (same client build, same source content)
   * resolve locally without a network round trip — every translation reaches us as deterministic JSON, so cache hit ⇒ identical result as the service would have returned. Cache key embeds
   * `Settings.VERSION` plus a SHA-256 fingerprint of the source content, so any deploy or any source-text edit triggers a fresh fetch automatically.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param source - The source language code (e.g., 'en').
   * @param target - The target language code (e.g., 'fr').
   * @param content - The content to translate (flat JSON object with string values).
   * @param context - Context hint for the translation model (e.g., 'voice receptionist dashboard UI').
   * @returns The translation result with translated content.
   * @throws Error when the request fails or responds with a non-OK status.
   */
  public async Translate(
    signal: AbortSignal,
    source: string,
    target: string,
    content: Record<string, string>,
    context: string
  ): Promise<{ source: string; target: string; content: Record<string, string> }> {
    // Cache layer is strictly best-effort. Any failure to build the key, read from IndexedDB, or parse the cached payload must degrade cleanly to the network path so callers running in
    // crypto.subtle-less contexts (insecure origins, older WebViews, file://) still get a working Translate.
    let cacheKey: string | null = null;
    try {
      cacheKey = await this.buildTranslateCacheKey(source, target, content, context);
    } catch (err: unknown) {
      // Defensive only — the SHA-256 path is gated on crypto.subtle availability and the FNV fallback is pure JS. Not actionable, so logged as Log.
      Logger.Log(localConstants.logContext, `${localConstants.logTranslateCacheKeyErr}${Logger.GetErrorMessage(err)}`);
    }
    if (cacheKey !== null) {
      let cached: string | null = null;
      try {
        cached = await GetIndexedDB(cacheKey);
      } catch (err: unknown) {
        // Defensive only — GetIndexedDB swallows storage errors internally and returns null. Not actionable, so logged as Log.
        Logger.Log(localConstants.logContext, `${localConstants.logTranslateCacheReadErr}${Logger.GetErrorMessage(err)}`);
      }
      if (cached !== null) {
        try {
          return JSON.parse(cached) as { source: string; target: string; content: Record<string, string> };
        } catch (err: unknown) {
          // Corrupt entry — evict and fall through to network. Don't await the delete; a stale entry that overwrites cleanly on the success path costs nothing.
          Logger.Warn(localConstants.logContext, `${localConstants.logTranslateCacheParseErr}${Logger.GetErrorMessage(err)}`);
          void DeleteIndexedDB(cacheKey);
        }
      }
    }
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, 'localization/translate', {
      method: FrontendConstants.MethodPost,
      headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson, 'X-Client-Version': Settings.VERSION },
      body: JSON.stringify({ source, target, content, context }),
      signal
    });
    if (!res.ok) throw new Error('Failed to translate content');
    const result = (await res.json()) as { source: string; target: string; content: Record<string, string> };
    // Fire-and-forget cache write — never block the UI on storage I/O. Failures inside SetIndexedDB are already logged. Skip entirely when the cache key couldn't be built.
    if (cacheKey !== null) void SetIndexedDB(cacheKey, JSON.stringify(result));
    return result;
  }

  /**
   * Builds the IndexedDB cache key for a translation request. The key combines the client build version (so a deploy globally invalidates every cached entry) with a fingerprint of the
   * source-language content, target language, and context — any change to source text yields a different fingerprint and a cache miss, so the cache can never serve a stale translation for edited
   * content.
   *
   * Prefers `crypto.subtle.digest('SHA-256', ...)` when available (modern HTTPS / secure-context browsers). Falls back to a synchronous FNV-1a fingerprint when `crypto.subtle` is missing
   * (non-secure origins, older WebViews, file://) so the cache still works in those environments. The algorithm tag is embedded in the cache key so entries from the two paths never alias.
   *
   * @param source - The source language code.
   * @param target - The target language code.
   * @param content - The source content map.
   * @param context - The translation context hint.
   * @returns The fully-namespaced cache key.
   */
  private async buildTranslateCacheKey(source: string, target: string, content: Record<string, string>, context: string): Promise<string> {
    const sortedKeys = Object.keys(content).sort();
    const serialised = sortedKeys.map((key) => `${key}${localConstants.fingerprintFieldDelimiter}${content[key]}`).join(localConstants.fingerprintEntryDelimiter);
    const payload = `${source}${localConstants.fingerprintFieldDelimiter}${target}${localConstants.fingerprintFieldDelimiter}${context}${localConstants.fingerprintFieldDelimiter}${serialised}`;
    const subtle = typeof crypto !== 'undefined' ? crypto.subtle : undefined;
    let algorithmTag: string;
    let fingerprint: string;
    if (subtle && typeof subtle.digest === 'function') {
      const bytes = new TextEncoder().encode(payload);
      const digest = await subtle.digest(localConstants.hashAlgorithmSha256, bytes);
      fingerprint = Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
        .substring(0, localConstants.fingerprintLength);
      algorithmTag = localConstants.fingerprintAlgorithmTagSha;
    } else {
      fingerprint = this.computeFnvFingerprint(payload);
      algorithmTag = localConstants.fingerprintAlgorithmTagFnv;
    }
    const sep = localConstants.translateCacheKeySeparator;
    return `${localConstants.translateCachePrefix}${algorithmTag}${sep}${Settings.VERSION}${sep}${source}${sep}${target}${sep}${context}${sep}${fingerprint}`;
  }

  /**
   * Synchronous JS fallback fingerprint used when `crypto.subtle` is unavailable. Runs two FNV-1a 32-bit passes (forward bytes + reversed bytes) and concatenates the hex output to match the
   * `fingerprintLength` width of the SHA-256 truncation path. Collision resistance is much lower than SHA-256, but more than adequate for a per-client cache scoped by Settings.VERSION and
   * (source, target, context).
   *
   * @param payload - The serialised translation request payload to fingerprint.
   * @returns Hex fingerprint of `fingerprintLength` characters.
   */
  private computeFnvFingerprint(payload: string): string {
    // Two passes provide ~64 effective bits and stop trivial palindrome collisions (e.g. "ab" vs "ba" reverse). Both passes are over the UTF-8 byte stream so multi-byte glyphs hash consistently.
    const bytes = new TextEncoder().encode(payload);
    let forward = localConstants.fnvOffsetBasis;
    let reverse = localConstants.fnvOffsetBasis;
    for (let i = 0; i < bytes.length; i++) {
      forward = Math.imul(forward ^ bytes[i], localConstants.fnvPrime) >>> 0;
      reverse = Math.imul(reverse ^ bytes[bytes.length - 1 - i], localConstants.fnvPrime) >>> 0;
    }
    const forwardHex = forward.toString(16).padStart(localConstants.fnvHexPadLength, '0');
    const reverseHex = reverse.toString(16).padStart(localConstants.fnvHexPadLength, '0');
    return `${forwardHex}${reverseHex}`.substring(0, localConstants.fingerprintLength);
  }
}
